import React, { useState } from "react";
import { ThumbsUpIcon, ThumbsDownIcon } from "../common/Icons";
import { submitFeedback } from "../../services/apiClientV2";
import { logError } from "../../utils/logging";
import styles from "./FeedbackButtons.module.css";

interface FeedbackButtonsProps {
  messageId: string;
  initialFeedback?: 1 | -1 | null;
}

const FeedbackButtons: React.FC<FeedbackButtonsProps> = ({
  messageId,
  initialFeedback = null,
}) => {
  const [feedback, setFeedback] = useState<1 | -1 | null>(initialFeedback);
  const [isLoading, setIsLoading] = useState(false);

  const handleFeedback = async (value: 1 | -1) => {
    if (isLoading) return;

    // Optimistic update
    const previousFeedback = feedback;
    
    // Toggle: if clicking same value, remove feedback (set to null - though backend might not support "unvote" yet, we can UI-wise)
    // Since backend upserts, we can't easily "delete" feedback unless we send neutral.
    // For now, let's assume we just switch or set. If same, maybe do nothing or allow toggle if supported.
    // Let's just update to new value. If same, maybe ignore? 
    // UX: usually clicking active thumb untoggles it.
    
    const newValue = value === feedback ? null : value;
    
    // If unvoting is not supported by API yet, we might just re-submit same value (no-op) or restrict UI.
    // Assuming for now we just support switching or setting. 
    // If newValue is null, we'd strictly need a delete endpoint or upsert with null?
    // The schema says value is 1 or -1. So we can't send null.
    // So we can't unvote in this version. We will just allow switching.
    
    if (newValue === null) {
        // If user clicks active button, we ideally want to remove vote.
        // Since API doesn't support it yet, we'll just do nothing or show a toast.
        // Let's just do nothing for now to avoid error.
        return; 
    }

    setFeedback(newValue);
    setIsLoading(true);

    try {
      await submitFeedback(messageId, newValue);
    } catch (error) {
      logError("Failed to submit feedback", error);
      // Revert on error
      setFeedback(previousFeedback);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <button
        type="button"
        className={`${styles.feedbackButton} ${feedback === 1 ? styles.active : ""}`}
        onClick={() => handleFeedback(1)}
        title="Îmi place"
        aria-label="Îmi place"
        disabled={isLoading}
      >
        <ThumbsUpIcon />
      </button>
      <button
        type="button"
        className={`${styles.feedbackButton} ${feedback === -1 ? styles.active : ""}`}
        onClick={() => handleFeedback(-1)}
        title="Nu îmi place"
        aria-label="Nu îmi place"
        disabled={isLoading}
      >
        <ThumbsDownIcon />
      </button>
    </div>
  );
};

export default FeedbackButtons;

